import React, { useState, useEffect, useCallback } from "react";
import Modal from "./Modal";
import "./BuffetBookings.css";

const SLOT_LABELS = {
  1: "Slot 1 (2-4 pm)",
  2: "Slot 2 (4:30-6:30 pm)",
};

const BuffetBookings = ({ setIsAnyModalActive }) => {
  const [selectedDate, setSelectedDate] = useState("");
  const [bookings, setBookings] = useState({});
  const [slotsInfo, setSlotsInfo] = useState({});
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [moveDropdownId, setMoveDropdownId] = useState(null);
  const [modalConfig, setModalConfig] = useState(null);
  const [error, setError] = useState("");

  const setErrorWithTimeout = (msg) => {
    setError(msg);
    if (msg) setTimeout(() => setError(""), 3000);
  };

  const getNextSunday = () => {
    const now = new Date();
    const dow = now.getDay();
    const daysUntil = dow === 0 ? 0 : 7 - dow;
    const sunday = new Date(now);
    sunday.setDate(now.getDate() + daysUntil);
    const yyyy = sunday.getFullYear();
    const mm = String(sunday.getMonth() + 1).padStart(2, "0");
    const dd = String(sunday.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  useEffect(() => {
    setSelectedDate(getNextSunday());
  }, []);

  const fetchBookings = useCallback(async (date, isBackground = false) => {
    if (!isBackground) setLoading(true);
    try {
      const res = await fetch(
        `https://admin-new.lasgidirestaurant.com/php/get-bookings.php?booking_date=${date}`
      );
      const data = await res.json();
      if (data.success) {
        setBookings(data.bookings);
        setSlotsInfo(data.slots_info);
      } else {
        setErrorWithTimeout(data.message || "Failed to fetch data.");
      }
    } catch {
      setErrorWithTimeout("Failed to fetch bookings.");
    } finally {
      if (!isBackground) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedDate) fetchBookings(selectedDate);
  }, [selectedDate, fetchBookings]);

  useEffect(() => {
    if (!selectedDate) return;
    const interval = setInterval(() => fetchBookings(selectedDate, true), 5000);
    return () => clearInterval(interval);
  }, [selectedDate, fetchBookings]);

  const handleArrivedToggle = async (id, current) => {
    setActionLoading(true);
    try {
      const res = await fetch("https://admin-new.lasgidirestaurant.com/php/update-booking.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id, arrived: current ? 0 : 1 }),
      });
      const data = await res.json();
      if (data.success) fetchBookings(selectedDate);
      else setErrorWithTimeout(data.message || "Update failed.");
    } catch {
      setErrorWithTimeout("Update error.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (id) => {
    setActionLoading(true);
    try {
      const res = await fetch("https://admin-new.lasgidirestaurant.com/php/delete-booking.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id }),
      });
      const data = await res.json();
      if (data.success) fetchBookings(selectedDate);
      else setErrorWithTimeout(data.message || "Delete failed.");
    } catch {
      setErrorWithTimeout("Delete error.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleMove = async (id, fromSlot, toSlot) => {
    if (
      fromSlot === toSlot ||
      !slotsInfo[toSlot] ||
      slotsInfo[toSlot].remaining <= 0
    ) {
      setErrorWithTimeout("Invalid move or slot full.");
      return;
    }

    setActionLoading(true);
    try {
      const res = await fetch("https://admin-new.lasgidirestaurant.com/php/move-booking.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id, new_slot_id: toSlot }),
      });
      const data = await res.json();
      if (data.success) {
        fetchBookings(selectedDate);
        setMoveDropdownId(null);
      } else {
        setErrorWithTimeout(data.message || "Move failed.");
      }
    } catch {
      setErrorWithTimeout("Move error.");
    } finally {
      setActionLoading(false);
    }
  };

  const openConfirmModal = (title, onConfirm, content = null) => {
    setModalConfig({ title, onConfirm, content });
    setIsAnyModalActive(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const closeModal = () => {
    setModalConfig(null);
    setIsAnyModalActive(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const isPast =
    selectedDate &&
    new Date(selectedDate) < new Date(new Date().setHours(0, 0, 0, 0));

  return (
    <div className="page-container profiles-main">
      <div className="admin-container">
        <div className="date-picker">
          <label>Select Sunday: </label>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
          />
        </div>

        {(loading || actionLoading) && (
          <div className="spinner-overlay">
            <div className="spinner"></div>
          </div>
        )}

        {error && <p className="error-message">{error}</p>}

        {!loading && (
          <div className="slots-container">
            {Object.keys(SLOT_LABELS).map((slotId) => {
              const slotBookings = bookings[slotId] || [];
              const sorted = [...slotBookings].sort(
                (a, b) => a.arrived - b.arrived
              );

              return (
                <div key={slotId} className="slot-card">
                  <h2>
                    {SLOT_LABELS[slotId]}{" "}
                    {!isPast && slotsInfo[slotId] && (
                      <span className="availability">
                        ({slotsInfo[slotId].remaining} slots left)
                      </span>
                    )}
                  </h2>

                  {sorted.length ? (
                    sorted.map((b) => (
                      <div
                        key={b.id}
                        className={`booking-card ${b.arrived ? "arrived" : ""}`}
                      >
                        <p>
                          <strong>Name:</strong> {b.name}
                        </p>
                        <p>
                          <strong>Adults:</strong> {b.num_adults}{" "}
                          {b.num_adults > 1 ? "adults" : "adult"}
                        </p>
                        <p>
                          <strong>Children:</strong> {b.num_children}{" "}
                          {b.num_children !== 1 ? "children" : "child"}
                        </p>
                        <p>
                          <strong>Phone:</strong> {b.phone}
                        </p>
                        <p>
                          <strong>Email:</strong> {b.email}
                        </p>

                        <div className="booking-actions">
                          <button
                            className="arrived-btn"
                            onClick={() =>
                              openConfirmModal(
                                "Mark Booking as Arrived",
                                () => handleArrivedToggle(b.id, b.arrived),
                                <div className="delete-modal-heading">
                                  <p>
                                    Are you sure you want to mark booking for{" "}
                                    <strong>{b.name}</strong> as{" "}
                                    {b.arrived ? "not arrived" : "arrived"}?
                                  </p>
                                </div>
                              )
                            }
                            title="Toggle Arrived"
                            disabled={isPast}
                          >
                            <i className="fa fa-check"></i>
                          </button>

                          <button
                            className="move-btn"
                            onClick={() =>
                              setMoveDropdownId(
                                moveDropdownId === b.id ? null : b.id
                              )
                            }
                            title="Move Booking"
                            disabled={isPast}
                          >
                            <i className="fa fa-arrows-alt-h"></i>
                          </button>

                          {moveDropdownId === b.id && !isPast && (
                            <select
                              defaultValue=""
                              onChange={(e) =>
                                openConfirmModal(
                                  "Move Booking",
                                  () =>
                                    handleMove(
                                      b.id,
                                      parseInt(slotId),
                                      parseInt(e.target.value)
                                    ),
                                  <div className="delete-modal-heading">
                                    <p>
                                      Move booking for <strong>{b.name}</strong>{" "}
                                      ({b.num_adults} adult
                                      {b.num_adults > 1 ? "s" : ""},{" "}
                                      {b.num_children} child
                                      {b.num_children !== 1 ? "ren" : ""}) to{" "}
                                      <strong>
                                        {SLOT_LABELS[e.target.value]}
                                      </strong>
                                      ?
                                    </p>
                                  </div>
                                )
                              }
                            >
                              <option value="" disabled>
                                Move to...
                              </option>
                              {Object.keys(SLOT_LABELS)
                                .filter((s) => s !== slotId)
                                .map((s) => (
                                  <option
                                    key={s}
                                    value={s}
                                    disabled={slotsInfo[s]?.remaining <= 0}
                                  >
                                    {SLOT_LABELS[s]}{" "}
                                    {slotsInfo[s]?.remaining <= 0
                                      ? "(Full)"
                                      : ""}
                                  </option>
                                ))}
                            </select>
                          )}

                          <button
                            className="delete-btn"
                            onClick={() =>
                              openConfirmModal(
                                "Delete Booking",
                                () => handleDelete(b.id),
                                <div className="delete-modal-heading">
                                  <p>
                                    Are you sure you want to delete booking for{" "}
                                    <strong>{b.name}</strong>?
                                  </p>
                                </div>
                              )
                            }
                            title="Delete Booking"
                            disabled={isPast}
                          >
                            <i className="fa fa-trash"></i>
                          </button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p>No bookings for this slot.</p>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {modalConfig && (
          <Modal
            title={modalConfig.title}
            onClose={closeModal}
            onSubmit={(e) => {
              e.preventDefault();
              modalConfig.onConfirm();
              closeModal();
            }}
            submitButtonText="Confirm"
            showCancel
            showSubmitButton
            className="delete-modal-content"
          >
            {modalConfig.content}
          </Modal>
        )}
      </div>
    </div>
  );
};

export default BuffetBookings;
